import { Spinner, VStack } from "@chakra-ui/react"
import { flattenEdges } from "@pathwright/utils"
import { AnimatePresence } from "framer-motion"
import { useEffect } from "react"
import {
  HomeRegistrationFragment,
  HomeRegistrationFragmentDoc,
  HomeRegistrationsQueryVariables,
  InboxPendingReviewTarget,
  useHomeRegistrationsQuery,
  useInboxPendingReviewCountsQuery,
  usePositioningContextQuery
} from "../../api/generated"
import {
  PositioningContextProvider,
  sortPositionedObjects,
  usePositioningContext
} from "../../positioning/PositioningContext"
import PathCard from "../components/pathCard/PathCard"
import PathCardContainer from "../components/pathCard/PathCardContainer"
import CopyLinkAddPromptCard, {
  useResourceCopyConfigLinks
} from "../components/prompt/CopyLinkAddPromptCard"
import CopyPathPromptCard from "../components/prompt/CopyPathPromptCard"
import LayoutItem, { PositionedLayoutItem } from "../components/ui/LayoutItem"
import { useHomeTip } from "../tips/HomeTips"
import { useSuperUserHomeTips } from "../tips/SuperUserHomeTips"
import {
  filterNestedRegistration,
  filterOutSourceRegistration,
  filterRootRegistration
} from "../utils"

// We'll use this util to "migrate" registrations to the new positioning system.
// This is only necessary to maintain similar ordering used on the old home screen.
// The old home screen orders most recent first with a separate section for completed
// registrations. The new home screen orders most recent last with no respect for
// completed registrations.
function SortRegistrations({
  registrations,
  onSortComplete
}: {
  registrations: HomeRegistrationFragment[]
  onSortComplete?: () => void
}) {
  const { updatePositionedObject } = usePositioningContext()

  useEffect(() => {
    if (registrations.length) {
      // FIrst we sort the registrations according to the old home screen order.
      const sortedRegistrations = registrations.sort((a, b) => {
        // Sort by non-completed registrations first, then by most recent.
        if (a.completion_state !== 3 && b.completion_state === 3) return -1
        if (a.completion_state === 3 && b.completion_state !== 3) return 1
        // If both are completed or not completed, sort by most recent.
        return new Date(b.created).getTime() - new Date(a.created).getTime()
      })

      // Now, we assign positions to the registrations to enforce the new sort order (as a default).
      // We position the last item to the last index, which will generate positions for all preceding
      // items as well.
      const index = sortedRegistrations.length - 1
      updatePositionedObject({
        list: sortedRegistrations,
        item: sortedRegistrations[index],
        index: index,
        onCompleted: onSortComplete
      })
    }
  }, [registrations.length])

  return null
}

function Loading() {
  return (
    <VStack
      className="UserPathsList__loading"
      align="center"
      w="full"
      p={2}
      minH="50vh"
      justifyContent="center"
    >
      <Spinner size="lg" color="whiteAlpha.900" />
    </VStack>
  )
}

const RegistrationsContainer = ({
  variables
}: {
  variables: HomeRegistrationsQueryVariables
}) => {
  const {
    data,
    previousData,
    loading: registrationsLoading
  } = useHomeRegistrationsQuery({
    variables
  })
  const positioningContextQuery = usePositioningContextQuery({
    variables: {
      input: variables.positioningContext!
    },
    skip: !variables.positioningContext
  })
  const hasPositionedHomeScreen =
    !!positioningContextQuery.data?.positioningContext?.id

  // Fall back to previous data if no current data exists (if registrations are loading again).
  const registrationNodes =
    (data?.me?.registrations || previousData?.me?.registrations)?.edges?.map(
      (edge) => edge?.node!
    ) || []

  const registrations = sortPositionedObjects(
    filterOutSourceRegistration(registrationNodes)
  )
  // Are we waiting on registrations to be positioned (a migration from the old home screen to the new one)?
  // Checking for more than 1 registration since positioning 1 registration will not change it's visual position.
  // This helps avoid showing a spinner after the user creates their first Path.
  const awaitingPositionedRegistrations =
    registrations.length > 1 && !hasPositionedHomeScreen

  // Get all tips, merged into one list, iterate through and render wrappring LayoutItem.
  const superUserHomeTips = useSuperUserHomeTips({ registrations })
  const homeTip = useHomeTip({ registrations })
  const tips = [...superUserHomeTips, homeTip].filter(Boolean)

  // Get all copy links, iterate through and render wrappring LayoutItem.
  const [copyLinks, { loading: copyLinksLoading }] =
    useResourceCopyConfigLinks()

  const inboxPendingReviewCountsQuery = useInboxPendingReviewCountsQuery({
    variables: {
      targetType: InboxPendingReviewTarget.Path,
      targetIds: {
        in: registrations.map((registration) => registration.group.id)
      }
    },
    skip: !registrations.length
  })
  const pendingReviewCounts = (flattenEdges(
    inboxPendingReviewCountsQuery.data?.inboxPendingReviewConnections
  ) || []) as {
    id: number
    count: number
  }[]

  const massagedRegistrations = registrations.map((registration) => {
    const pendingReviewCount =
      pendingReviewCounts.find(({ id }) => id === registration.group.id)
        ?.count || 0

    return {
      ...registration,
      pendingReviewCount
    }
  })

  // Focus added PathCard.
  // NOTE: this isn't quite working after the refactor, most notably due to the fact
  // that the data reload after completion happens too "late". This useEffect runs before
  // the created Path is reloaded. A better option might be to use a general purpose hook
  // for capturing an "added" item to a apollo query hook data.
  // const { pathCreatorState } = usePathCreatorContext()
  // useEffect(() => {
  //   if (pathCreatorState.status === "complete") {
  //     // When no other element is focused (or default body).
  //     if (!document.hasFocus() || document.activeElement?.tagName === "BODY") {
  //       const link = document.querySelector(
  //         `#PathCard__${pathCreatorState.cohort.id} .PathCard__link-overlay`
  //       ) as HTMLElement
  //       link?.focus()
  //     }
  //   }
  // }, [pathCreatorState.status])

  const loading =
    registrationsLoading || copyLinksLoading || awaitingPositionedRegistrations

  return (
    <PositioningContextProvider
      items={registrations}
      itemKey="registration"
      itemFragmentDocument={HomeRegistrationFragmentDoc}
      context={variables.positioningContext!}
    >
      <>
        {!positioningContextQuery.loading && !hasPositionedHomeScreen && (
          <SortRegistrations
            registrations={registrations}
            // We refetch the positioning context when sorting is complete to reasses if
            // a hasPositionedHomeScreen.
            onSortComplete={positioningContextQuery.refetch}
          />
        )}
        {loading ? (
          <Loading />
        ) : (
          <VStack className="UserPathsList" w="100%" h="100%" pos="relative">
            <VStack
              as="ul"
              spacing={3}
              position="relative"
              w="full"
              h="full"
              align="flex-start"
              justify="flex-start"
              // Certain Tip components will cause a parent container to exceed the expected
              // width due to text wrapping. This is likely caused by a flex layout that attempts
              // to fit the text within the container even if it does wrap.
              // Current fix (only one that has worked so far) is to set a max width on this list.
              maxW="500px"
            >
              {/* Render all animatable/layout items as direct, keyed children. */}
              <AnimatePresence
                // Disable animation on initial mount as the items tend to stretch to their
                // full height, which isn't desirable.
                initial={false}
              >
                {tips.map((tip) => (
                  <LayoutItem key={tip?.key}>{tip}</LayoutItem>
                ))}
                <CopyPathPromptCard />
                {copyLinks.map((copyLink) => (
                  // Preferring to key the link via registration ID as that is the same key used
                  // for the registration items (idea is to prevent a layout animation when copied
                  // link is removed and copier registration is added to the registrations list.)
                  <LayoutItem
                    key={copyLink.copierRegistrationId || copyLink.id}
                  >
                    <CopyLinkAddPromptCard copyLink={copyLink} />
                  </LayoutItem>
                ))}
                {massagedRegistrations
                  .filter(filterRootRegistration)
                  .map((rootRegistration) => (
                    <PositionedLayoutItem
                      key={rootRegistration.id}
                      position={rootRegistration.position}
                    >
                      <PathCardContainer
                        registration={rootRegistration}
                        nestedRegistrations={massagedRegistrations.filter(
                          (registration) =>
                            filterNestedRegistration(
                              rootRegistration,
                              registration
                            )
                        )}
                        PathCardComponent={PathCard}
                      />
                    </PositionedLayoutItem>
                  ))}
              </AnimatePresence>
            </VStack>
          </VStack>
        )}
      </>
    </PositioningContextProvider>
  )
}

export default RegistrationsContainer
